<template>
    <div class="matches-toolbar">
        <div class="toolbar-left">
            <div class="search-box">
                <i class="icon-search4 search-icon"></i>
                <input
                    type="search"
                    v-model="search"
                    :placeholder="$t('labels.search')"
                    :aria-label="$t('labels.search_matches')"
                    class="search-input"
                >
            </div>
            <div class="pagination">
                <span class="pagination-range">{{ rangeStart }}–{{ rangeEnd }} {{ $t('labels.of') }} {{ activeCount }}</span>
                <button
                    type="button"
                    :aria-label="$t('buttons.previous_page')"
                    :disabled="!hasPrevPage"
                    class="pagination-btn"
                    @click="prevPage"
                >
                    <i class="icon-arrow-left12"></i>
                </button>
                <button
                    type="button"
                    :aria-label="$t('buttons.next_page')"
                    :disabled="!hasNextPage"
                    class="pagination-btn"
                    @click="nextPage"
                >
                    <i class="icon-arrow-right13"></i>
                </button>
            </div>
        </div>
        <div role="tablist" :aria-label="$t('labels.matches_filter')" class="tabs">
            <button
                type="button"
                role="tab"
                :aria-selected="activeTab === 'all'"
                :class="['tab', { active: activeTab === 'all' }]"
                @click="activeTab = 'all'"
            >
                {{ $t('labels.all') }}<span class="tab-count">{{ allCount }}</span>
            </button>
            <button
                type="button"
                role="tab"
                :aria-selected="activeTab === 'found'"
                :class="['tab', { active: activeTab === 'found' }]"
                @click="activeTab = 'found'"
            >
                {{ $t('labels.finned') }}<span class="tab-count">{{ foundCount }}</span>
            </button>
            <button
                type="button"
                role="tab"
                :aria-selected="activeTab === 'not_found'"
                :class="['tab', { active: activeTab === 'not_found' }]"
                @click="activeTab = 'not_found'"
            >
                {{ $t('labels.not_finned') }}<span class="tab-count">{{ notFoundCount }}</span>
            </button>
        </div>
    </div>
    <div class="table-scroll">
        <table class="table table-bordered table-fixed">
            <thead>
                <tr>
                    <th width="50%">Публікація</th>
                    <th width="50%">Товар</th>
                </tr>
            </thead>
            <tbody>
                <tr v-if="!isLoading && allCount === 0">
                    <td colspan="2" class="text-muted text-center">{{ $t('alerts.all_selected_products_already_connected') }}</td>
                </tr>
                <tr v-for="publication in publications" :key="publication.uuid">
                    <td>
                        <div class="publication-cell">
                            <img :src="publication.main_image_url ?? PLACEHOLDER_IMAGE_URL" class="publication-image">
                            <div>
                                <span v-if="publication.name">{{ publication.name }}</span>
                                <u v-else class="text-muted">{{ $t('placeholders.missing') }}</u>
                                <br>
                                <span class="text-muted">{{ publication.sku }} · {{ publication.category_name }}</span>
                            </div>
                        </div>
                    </td>
                    <td>
                        <div v-if="publication.product_by_sku" class="publication-cell">
                            <img :src="publication.product_by_sku.main_image_url ?? PLACEHOLDER_IMAGE_URL" class="publication-image">
                            <div>
                                <span>{{ publication.product_by_sku.name }}</span>
                                <br>
                                <span class="text-muted">{{ publication.product_by_sku.sku }} · {{ publication.product_by_sku.category_name }}</span>
                            </div>
                        </div>
                        <Autocomplete
                            v-else
                            url="/catalog/product-items"
                            :model-value="selectedProducts[publication.uuid]?.uuid"
                            :selected-value="selectedProducts[publication.uuid]?.name"
                            :can-be-empty="true"
                            @update:model-value="value => selectProduct(publication.uuid, value)"
                        />
                    </td>
                </tr>
            </tbody>
        </table>
    </div>
</template>
<script>
import Autocomplete from "../../../../components/new/form/autocomplete/AppAutoComplete.vue";

import { httpClient } from "../../../../services/HttpClient";
import { PLACEHOLDER_IMAGE_URL } from "../../../../constants";
import lodash from 'lodash';

export default {
    name: "MatchesReviewStep",
    components: { Autocomplete },
    props: {
        selectionMode: {
            type: Object,
            default: null
        },
        selectedUuids: {
            type: Array,
            default: () => []
        },
        filters: {
            type: Object,
            default: () => ({})
        },
        selectedProducts: {
            type: Object,
            default: () => ({})
        }
    },
    emits: ['update:selectedProducts'],
    data() {
        return {
            PLACEHOLDER_IMAGE_URL,
            search: '',
            activeTab: 'all',
            publications: [],
            isLoading: false,
            page: 1,
            perPage: 10,
            foundCount: 0,
            notFoundCount: 0
        }
    },
    computed: {
        publicationsUrl() {
            return `/projects/${this.$route.params.projectUuid}/pim/channels/${this.$route.params.integrationUuid}/connection-process/publications`;
        },
        baseParams() {
            return this.selectionMode?.value === 'selected'
                ? { uuids: this.selectedUuids }
                : this.filters;
        },
        allCount() {
            return this.foundCount + this.notFoundCount;
        },
        activeCount() {
            if (this.activeTab === 'found') {
                return this.foundCount;
            }

            if (this.activeTab === 'not_found') {
                return this.notFoundCount;
            }

            return this.allCount;
        },
        rangeStart() {
            return this.activeCount === 0 ? 0 : (this.page - 1) * this.perPage + 1;
        },
        rangeEnd() {
            return Math.min(this.page * this.perPage, this.activeCount);
        },
        hasPrevPage() {
            return this.page > 1;
        },
        hasNextPage() {
            return this.page * this.perPage < this.activeCount;
        }
    },
    methods: {
        selectProduct(publicationUuid, product) {
            this.$emit('update:selectedProducts', { ...this.selectedProducts, [publicationUuid]: product });
        },
        onActivate() {
            this.page = 1;
            this.fetchCounts();
            this.fetchPublications();
        },
        prevPage() {
            if (this.hasPrevPage) {
                this.page--;
                this.fetchPublications();
            }
        },
        nextPage() {
            if (this.hasNextPage) {
                this.page++;
                this.fetchPublications();
            }
        },
        async fetchCounts() {
            const [foundResponse, notFoundResponse] = await Promise.all([
                httpClient.get(this.publicationsUrl, { ...this.baseParams, has_product_by_sku: 1, limit: 1 }),
                httpClient.get(this.publicationsUrl, { ...this.baseParams, has_product_by_sku: 0, limit: 1 }),
            ]);

            this.foundCount = foundResponse.data.meta.total;
            this.notFoundCount = notFoundResponse.data.meta.total;
        },
        async fetchPublications() {
            this.isLoading = true;

            const params = { ...this.baseParams, page: this.page, limit: this.perPage };

            if (this.search) {
                params.search = this.search;
            }

            if (this.activeTab === 'found') {
                params.has_product_by_sku = 1;
            } else if (this.activeTab === 'not_found') {
                params.has_product_by_sku = 0;
            }

            try {
                const response = await httpClient.get(this.publicationsUrl, params);
                this.publications = response.data.data;
            } finally {
                this.isLoading = false;
            }
        }
    },
    watch: {
        activeTab() {
            this.page = 1;
            this.fetchPublications();
        },
        search: lodash.debounce(function () {
            this.page = 1;
            this.fetchPublications();
        }, 300)
    }
}
</script>
<style scoped>
.table-scroll {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
}
.table-bordered {
    border-top: 0px;
}
.matches-toolbar {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 1.5rem;
    border-bottom: 1px solid #e5e7eb;
}
.toolbar-left {
    display: flex;
    align-items: center;
    gap: 1.25rem;
    padding: 0.75rem 0 0.5rem;
}
.search-box {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    width: 220px;
    height: 30px;
    padding: 0 0.25rem;
}
.search-icon {
    flex-shrink: 0;
    font-size: 14px;
    color: #9aa2ad;
}
.search-input {
    width: 100%;
    height: 100%;
    background: transparent;
    border: none;
    font-size: 13px;
    color: #3c4553;
}
.search-input:focus {
    outline: none;
}
.search-input::placeholder {
    color: #9aa2ad;
}
.pagination {
    display: flex;
    align-items: center;
    gap: 0.25rem;
}
.pagination-range {
    margin-right: 0.25rem;
    font-size: 12px;
    font-variant-numeric: tabular-nums;
    color: #7b8492;
}
.pagination-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    border: none;
    border-radius: 0.25rem;
    background: transparent;
    color: #7b8492;
    font-size: 14px;
    outline: none;
    transition: background-color 0.15s ease-out;
}
.pagination-btn:hover:not(:disabled) {
    background-color: #f0f1f3;
}
.pagination-btn:disabled {
    pointer-events: none;
    color: #c9cdd3;
}
.tabs {
    display: flex;
    align-items: flex-end;
    gap: 1.5rem;
}
.tab {
    margin-bottom: -1px;
    padding: 1rem 0 0.75rem;
    border: none;
    border-bottom: 2px solid transparent;
    background: transparent;
    outline: none;
    font-size: 13px;
    color: #7b8492;
    transition: color 0.15s ease-out, border-color 0.15s ease-out;
}
.tab:hover {
    color: #3c4553;
}
.tab.active {
    border-bottom-color: #2f80ed;
    color: #2f80ed;
    font-weight: 500;
}
.tab-count {
    margin-left: 0.375rem;
    font-size: 12px;
    color: #9aa2ad;
}
.tab.active .tab-count {
    color: #2f80ed;
}
.publication-cell {
    display: flex;
    align-items: center;
    gap: 0.75rem;
}
.publication-image {
    width: 40px;
    height: 40px;
    object-fit: contain;
    background-color: #f5f5f5;
    border-radius: 4px;
    flex-shrink: 0;
}
</style>
